/**
 * Channel-link landing page — `/link/channel?token=…`.
 *
 * Destination of the "link your account" card a channel bot (Slack/Teams)
 * sends to a sender it can't place. The whole job finishes here, and for a
 * signed-in member it finishes without them touching anything: the page
 * redeems the card's token and shows the result.
 *
 * There is no provider consent step, deliberately. The bot already knows which
 * channel account is speaking — the inbound message is the proof — so the only
 * open question is which platform user it belongs to, and the session answers
 * that. Sending someone through Slack's or Microsoft's OAuth screen to
 * re-establish a fact we already hold is pure friction.
 *
 * - Member of that workspace → redeemed on arrival, confirmation shown.
 * - Signed in but not a member → explain that a workspace admin must invite
 *   them (membership is only ever granted by invitation or workspace SSO —
 *   this page must never create it), offering that workspace's SSO sign-in
 *   when it has one.
 * - Signed out → sign-in / create-account, both returning here afterwards.
 *
 * Lives OUTSIDE the workspace-scoped app shell (like /invitation) so a
 * session with zero workspaces can land here without being bounced to
 * /workspace/create — creating a fresh personal workspace is exactly the
 * wrong outcome for someone trying to reach an existing workspace's agent.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { AlertTriangle, Building2, CheckCircle2, Home, Loader2, LogIn, RefreshCw, UserPlus } from "lucide-react"

import logoImage from "@/assets/images/logo.png"
import logoImageDark from "@/assets/images/logo-dark.png"
import { Button } from "@/components/ui/button"
import { providerDisplayName } from "@/components/external-apis/provider-logos"
import { api } from "@/utils/api-client"
import { clearAuthTokens, isTokenValid, isRefreshTokenValid } from "@/utils/auth-storage"

/** Resolve response from GET /api/channel-link/resolve. */
interface ChannelLinkContext {
  workspaceId: string
  workspaceName: string
  provider: string
  agentName: string | null
  sso: {
    enabled: boolean
    slug: string | null
    /** 'required' means password sign-in is refused server-side. */
    enforcement: "optional" | "required"
    identityProvider: string | null
  }
}

/**
 * Whether this workspace accepts email/password at all.
 *
 * With `enforcement: 'required'` the server 403s password sign-in
 * (routes/auth/signin.ts), so rendering a Sign in / Create account button
 * would be a fake affordance — the click could only ever fail.
 */
function allowsPasswordAuth(ctx: ChannelLinkContext): boolean {
  return !(ctx.sso.enabled && ctx.sso.enforcement === "required")
}

/** Label for the SSO button — Azure AD is the only provider implemented today. */
function ssoButtonLabel(ctx: ChannelLinkContext): string {
  return ctx.sso.identityProvider === "azure_ad" || !ctx.sso.identityProvider
    ? "Sign in with Microsoft"
    : "Sign in with SSO"
}

/**
 * sessionStorage key holding this page's URL while the user detours through
 * the workspace's SSO sign-in. Only a same-origin fallback now: the return
 * path is passed to the server as `redirect_uri`, which also survives a
 * workspace whose SSO callback lands on its own tenant host.
 */
export const PENDING_CHANNEL_LINK_KEY = "taqat_pending_channel_link"

type PageState =
  | { kind: "loading"; message?: string }
  | { kind: "invalid"; message: string }
  | { kind: "signedOut"; ctx: ChannelLinkContext; ssoRetry: boolean }
  | { kind: "notMember"; ctx: ChannelLinkContext; email: string }
  | { kind: "linked"; ctx: ChannelLinkContext }

/**
 * Marks that we already sent this token's visitor off to authenticate.
 *
 * A signed-out visitor has exactly one thing to do, so this page shouldn't
 * make them click a button to get to it — it goes straight to the identity
 * provider, or straight to sign-in.
 *
 * The auto-redirect must fire at most once per token, and the guard is what
 * makes that safe. Both destinations can send the user back here without a
 * session — Azure's failure path lands on /signin, and a half-valid token can
 * have /signin bounce back — so redirecting unconditionally would be an
 * invisible loop with no way out. On the second arrival we render the choice
 * screen instead, which doubles as the retry affordance.
 */
function autoRedirectKey(token: string): string {
  return `taqat_channel_link_sso_tried:${token.slice(-24)}`
}

export function LinkChannelPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") ?? ""
  const [state, setState] = useState<PageState>({ kind: "loading" })
  // Guards the evaluate effect against React 18 double-invocation.
  const startedRef = useRef(false)

  const selfPath = `/link/channel?token=${encodeURIComponent(token)}`

  /**
   * Sign-in URL that comes back here, and — when the workspace has SSO — lets
   * the sign-in page offer it. Cards point at the canonical host, which has no
   * tenant subdomain for /signin to infer the workspace from.
   */
  const signinPath = useCallback(
    (ctx?: ChannelLinkContext) => {
      const ssoParam =
        ctx?.sso.enabled && ctx.sso.slug ? `&ssoSlug=${encodeURIComponent(ctx.sso.slug)}` : ""
      return `/signin?redirectUrl=${encodeURIComponent(selfPath)}${ssoParam}`
    },
    [selfPath],
  )

  /** Detour through the workspace's SSO; return here afterwards. */
  const signInWithSso = useCallback(
    (ctx: ChannelLinkContext) => {
      if (!ctx.sso.slug) return
      try {
        // Same-origin fallback, for the case where the server declines to
        // honour the return URL below. Time-bounded: on an SSO failure the user
        // never reaches the app, so this would otherwise linger and yank them
        // here on some unrelated later sign-in.
        sessionStorage.setItem(
          PENDING_CHANNEL_LINK_KEY,
          JSON.stringify({ path: selfPath, exp: Date.now() + 10 * 60 * 1000 }),
        )
      } catch {
        // Without the stash the user still lands in the app post-SSO; they can
        // click the card again. Not worth blocking the sign-in over.
      }
      // Tell the server where to hand the session back. A workspace with its
      // own SSO `redirectUrl` sends Azure to the TENANT host, and the callback
      // lands the user there — a different origin, where the sessionStorage
      // stash above simply doesn't exist. Passing the return URL explicitly is
      // what makes the round trip work for those workspaces.
      const returnUrl = `${window.location.origin}${selfPath}`
      window.location.href =
        `/api/auth/sso/authorize?slug=${encodeURIComponent(ctx.sso.slug)}` +
        `&redirect_uri=${encodeURIComponent(returnUrl)}`
    },
    [selfPath],
  )

  /**
   * Member path: bind the card's channel identity to this session. The server
   * re-checks membership and owns the write, so there is nothing to choose
   * here and nothing to switch workspace for.
   */
  const redeem = useCallback(
    async (ctx: ChannelLinkContext) => {
      setState({ kind: "loading", message: `Linking your ${providerDisplayName(ctx.provider)} account…` })
      try {
        await api.post("/channel-link/redeem", { token }, { showErrorToast: false })
        setState({ kind: "linked", ctx })
      } catch (err) {
        setState({
          kind: "invalid",
          message:
            err instanceof Error && err.message
              ? err.message
              : "Could not link your account. Try the link again in a moment.",
        })
      }
    },
    [token],
  )

  /**
   * Send a signed-out visitor straight to wherever they need to authenticate.
   *
   * There is nothing to choose here that the sign-in page can't offer better,
   * so this page shouldn't stand between the card and the login form. Which
   * destination depends on what the workspace actually accepts:
   *
   * - SSO required → the identity provider. Password sign-in is refused
   *   server-side, so /signin would be a dead end.
   * - Otherwise → /signin, carrying the return path. When the workspace also
   *   has SSO, its slug goes along so the sign-in page can offer that button
   *   too: /signin infers SSO from a tenant subdomain, and cards always point
   *   at the canonical host where there is none to read.
   *
   * Guarded to fire once per token (see {@link autoRedirectKey}); on any
   * return trip we fall through to the choice screen so the user is never
   * stuck in a redirect they can't see or escape.
   */
  const routeSignedOut = useCallback(
    (ctx: ChannelLinkContext) => {
      const key = autoRedirectKey(token)
      let alreadyTried = false
      try {
        alreadyTried = sessionStorage.getItem(key) === "1"
        if (!alreadyTried) sessionStorage.setItem(key, "1")
      } catch {
        // No sessionStorage — show the choice screen rather than risk looping.
        alreadyTried = true
      }

      if (alreadyTried) {
        setState({ kind: "signedOut", ctx, ssoRetry: true })
        return
      }

      const ssoOnly = ctx.sso.enabled && ctx.sso.enforcement === "required" && ctx.sso.slug
      if (ssoOnly) {
        setState({
          kind: "loading",
          message: `Redirecting to ${ssoButtonLabel(ctx).replace(/^Sign in with /, "")}…`,
        })
        signInWithSso(ctx)
        return
      }

      setState({ kind: "loading", message: "Taking you to sign in…" })
      navigate(signinPath(ctx), { replace: true })
    },
    [token, signInWithSso, navigate, signinPath],
  )

  /** Resolve the token, then classify the visitor and route accordingly. */
  const evaluate = useCallback(async () => {
    if (!token) {
      setState({ kind: "invalid", message: "This link is missing its token." })
      return
    }

    let ctx: ChannelLinkContext
    try {
      const response = await api.get(
        `/channel-link/resolve?token=${encodeURIComponent(token)}`,
        { requiresAuth: false, showErrorToast: false },
      )
      ctx = response.data as ChannelLinkContext
    } catch (err) {
      setState({
        kind: "invalid",
        message:
          err instanceof Error && err.message
            ? err.message
            : "This link is invalid or has expired.",
      })
      return
    }

    if (!isTokenValid() && !isRefreshTokenValid()) {
      routeSignedOut(ctx)
      return
    }

    // Signed in (or restorable) — fetch the session to learn memberships.
    let email = ""
    let isMember = false
    try {
      const session = await api.get("/auth/session", { showErrorToast: false })
      const payload = session?.data ?? {}
      email = payload?.user?.email ?? ""
      const workspaces: Array<{ id?: string }> = Array.isArray(payload?.workspaces)
        ? payload.workspaces
        : payload?.workspace
          ? [payload.workspace]
          : []
      isMember = workspaces.some((w) => w?.id === ctx.workspaceId)
    } catch {
      // Stale/dead tokens — treat as signed out rather than dead-ending.
      clearAuthTokens()
      routeSignedOut(ctx)
      return
    }

    if (isMember) {
      await redeem(ctx)
      return
    }
    setState({ kind: "notMember", ctx, email })
  }, [token, redeem, routeSignedOut])

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    void evaluate()
  }, [evaluate])

  const recheck = useCallback(() => {
    startedRef.current = false
    setState({ kind: "loading" })
    void evaluate()
  }, [evaluate])

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-card shadow-xl rounded-2xl border border-border p-8">
            <div className="flex justify-center mb-6">
              <img src={logoImage} alt="Taqat AI Logo" className="h-10 w-auto block dark:hidden" />
              <img src={logoImageDark} alt="Taqat AI Logo" className="h-10 w-auto hidden dark:block" />
            </div>

            {state.kind === "loading" ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{state.message ?? "Checking your link…"}</p>
              </div>
            ) : null}

            {state.kind === "invalid" ? (
              <div className="space-y-4 text-center">
                <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
                <h1 className="text-xl font-semibold tracking-tight">This link didn't work</h1>
                <p className="text-sm text-muted-foreground">{state.message}</p>
                <p className="text-sm text-muted-foreground">
                  Go back to the chat and send the assistant another message — it will reply with a
                  fresh link.
                </p>
              </div>
            ) : null}

            {state.kind === "signedOut" ? (
              <div className="space-y-5">
                <div className="space-y-2 text-center">
                  <h1 className="text-xl font-semibold tracking-tight">Link your {providerDisplayName(state.ctx.provider)} account</h1>
                  <p className="text-sm text-muted-foreground">
                    <strong>{state.ctx.agentName ?? "An assistant"}</strong> in{" "}
                    <strong>{state.ctx.workspaceName}</strong> needs to know who you are before it
                    can reply. Sign in and we'll link it for you.
                  </p>
                </div>

                {/* Shown when we already bounced this token to the IdP and the
                    user came back without a session (cancelled, or Back). */}
                {state.ssoRetry ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                    That sign-in didn't finish. Try again below.
                  </div>
                ) : null}
                <div className="space-y-3">
                  {state.ctx.sso.enabled ? (
                    <Button className="w-full" onClick={() => signInWithSso(state.ctx)}>
                      <LogIn className="mr-2 h-4 w-4" />
                      {ssoButtonLabel(state.ctx)}
                    </Button>
                  ) : null}

                  {/* SSO-required workspaces stop here: password auth is refused
                      server-side, so those buttons would be dead ends. */}
                  {allowsPasswordAuth(state.ctx) ? (
                    <>
                      {state.ctx.sso.enabled ? (
                        <div className="relative py-1">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-border" />
                          </div>
                          <div className="relative flex justify-center">
                            <span className="bg-card px-2 text-xs uppercase text-muted-foreground">
                              or
                            </span>
                          </div>
                        </div>
                      ) : null}
                      <Button
                        variant={state.ctx.sso.enabled ? "outline" : "default"}
                        className="w-full"
                        onClick={() => navigate(signinPath(state.ctx))}
                      >
                        <LogIn className="mr-2 h-4 w-4" />
                        Sign in with email
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() =>
                          navigate(`/signup?redirectUrl=${encodeURIComponent(selfPath)}`)
                        }
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Create account
                      </Button>
                    </>
                  ) : (
                    <p className="text-center text-xs text-muted-foreground">
                      {state.ctx.workspaceName} requires single sign-on, so there's no separate
                      password to set up.
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            {/* Going back to the chat is the real next step, so the copy leads
                with it and the button stays secondary — it's an escape hatch
                for anyone who'd rather open the app than close the tab. */}
            {state.kind === "linked" ? (
              <div className="space-y-4 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
                <h1 className="text-xl font-semibold tracking-tight">You're connected</h1>
                <p className="text-sm text-muted-foreground">
                  Your {providerDisplayName(state.ctx.provider)} account is linked to{" "}
                  <strong>{state.ctx.workspaceName}</strong>.
                </p>
                <p className="text-sm text-muted-foreground">
                  Head back to your {providerDisplayName(state.ctx.provider)} chat with{" "}
                  <strong>{state.ctx.agentName ?? "the assistant"}</strong> and send your message
                  again — it will reply this time.
                </p>
                <Button variant="outline" className="w-full" onClick={() => navigate("/")}>
                  <Home className="mr-2 h-4 w-4" />
                  Go home
                </Button>
                <p className="text-xs text-muted-foreground">Or just close this tab.</p>
              </div>
            ) : null}

            {state.kind === "notMember" ? (
              <div className="space-y-5">
                <div className="space-y-2 text-center">
                  <Building2 className="mx-auto h-10 w-10 text-primary" />
                  <h1 className="text-xl font-semibold tracking-tight">
                    You're not in {state.ctx.workspaceName} yet
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    <strong>{state.ctx.agentName ?? "This assistant"}</strong> belongs to{" "}
                    <strong>{state.ctx.workspaceName}</strong>, and your account
                    {state.email ? (
                      <>
                        {" "}
                        (<strong>{state.email}</strong>)
                      </>
                    ) : null}{" "}
                    isn't a member of it.
                  </p>
                </div>

                <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Ask a <strong>{state.ctx.workspaceName}</strong> admin to invite
                  {state.email ? (
                    <>
                      {" "}
                      <strong>{state.email}</strong>
                    </>
                  ) : (
                    " you"
                  )}
                  . Once you've accepted the invitation, come back and click the link in{" "}
                  {providerDisplayName(state.ctx.provider)} again.
                </div>

                <div className="space-y-3">
                  {state.ctx.sso.enabled ? (
                    <Button className="w-full" onClick={() => signInWithSso(state.ctx)}>
                      <LogIn className="mr-2 h-4 w-4" />
                      {ssoButtonLabel(state.ctx)}
                    </Button>
                  ) : null}
                  <Button
                    variant={state.ctx.sso.enabled ? "outline" : "default"}
                    className="w-full"
                    onClick={recheck}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    I've been invited — check again
                  </Button>
                  {/* On SSO-required workspaces the SSO button above already IS the
                      switch-account path (the IdP offers its own picker), and the
                      password page would 403 — so don't offer it. */}
                  {allowsPasswordAuth(state.ctx) ? (
                    <p className="text-center text-xs text-muted-foreground">
                      Wrong account?{" "}
                      <Link
                        to={signinPath(state.ctx)}
                        className="text-primary underline-offset-4 hover:underline"
                        onClick={() => clearAuthTokens()}
                      >
                        Sign in with a different one
                      </Link>
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
